import type { TradeExplainId } from "../trade-explain";

export type TechnicalIndicatorFamily = "trend" | "momentum" | "volatility" | "participation" | "structure";

export type TechnicalIndicatorImplementation =
  | "sma"
  | "ema"
  | "rsi"
  | "macd"
  | "stochastic"
  | "atr_pct"
  | "bollinger"
  | "obv"
  | "breakout_atr";

export type TechnicalOutputUnit = "price" | "decimal" | "index" | "volume";

export type TechnicalIndicatorDefinition = {
  schemaVersion: "tradvera.technical-indicator.v1";
  id: string;
  version: string;
  family: TechnicalIndicatorFamily;
  implementation: TechnicalIndicatorImplementation;
  parameters: Record<string, number>;
  inputFields: Array<"open" | "high" | "low" | "close" | "volume">;
  outputFields: string[];
  outputUnits: Record<string, TechnicalOutputUnit>;
  warmupBars: number;
  seedPolicy: "simple_window" | "sma_then_recursive" | "wilder" | "first_observation";
  nullPolicy: "abstain";
  knownAtPolicy: "all_inputs_known_at_evaluation";
  confirmationPolicy: "propagate_latest_provisional";
  supportedTimeframes: ["4h", "1d", "1w", "1m"];
  explainId: TradeExplainId;
  goldenFixtureIds: string[];
};

const indicator = (
  id: string,
  family: TechnicalIndicatorFamily,
  implementation: TechnicalIndicatorImplementation,
  parameters: Record<string, number>,
  inputFields: TechnicalIndicatorDefinition["inputFields"],
  outputUnits: TechnicalIndicatorDefinition["outputUnits"],
  warmupBars: number,
  seedPolicy: TechnicalIndicatorDefinition["seedPolicy"],
  explainId: TradeExplainId,
): TechnicalIndicatorDefinition => ({
  schemaVersion: "tradvera.technical-indicator.v1",
  id,
  version: "1.0.0",
  family,
  implementation,
  parameters,
  inputFields,
  outputFields: Object.keys(outputUnits),
  outputUnits,
  warmupBars,
  seedPolicy,
  nullPolicy: "abstain",
  knownAtPolicy: "all_inputs_known_at_evaluation",
  confirmationPolicy: "propagate_latest_provisional",
  supportedTimeframes: ["4h", "1d", "1w", "1m"],
  explainId,
  goldenFixtureIds: ["technical.linear-40.v1"],
});

export const TECHNICAL_INDICATOR_CATALOG: TechnicalIndicatorDefinition[] = [
  indicator("technical.sma_20", "trend", "sma", { period: 20 }, ["close"], { sma: "price" }, 20, "simple_window", "technical.sma_20"),
  indicator("technical.ema_20", "trend", "ema", { period: 20 }, ["close"], { ema: "price" }, 20, "sma_then_recursive", "technical.ema_20"),
  indicator("technical.rsi_14", "momentum", "rsi", { period: 14 }, ["close"], { rsi: "index" }, 15, "wilder", "technical.rsi_14"),
  indicator("technical.macd_12_26_9", "momentum", "macd", { fast: 12, slow: 26, signal: 9 }, ["close"], { macd: "price", signal: "price", histogram: "price" }, 34, "sma_then_recursive", "technical.macd_12_26_9"),
  indicator("technical.stochastic_14_3_3", "momentum", "stochastic", { period: 14, smoothK: 3, smoothD: 3 }, ["high", "low", "close"], { k: "index", d: "index" }, 18, "simple_window", "technical.stochastic_14_3_3"),
  indicator("technical.atr_pct_14", "volatility", "atr_pct", { period: 14 }, ["high", "low", "close"], { atr: "price", atrPct: "decimal" }, 15, "wilder", "technical.atr_pct_14"),
  indicator("technical.bollinger_20_2", "volatility", "bollinger", { period: 20, multiplier: 2 }, ["close"], { middle: "price", upper: "price", lower: "price", bandwidth: "decimal", percentB: "decimal" }, 20, "simple_window", "technical.bollinger_20_2"),
  indicator("technical.obv", "participation", "obv", {}, ["close", "volume"], { obv: "volume" }, 2, "first_observation", "technical.obv"),
  indicator("technical.breakout_atr_20_14", "structure", "breakout_atr", { lookback: 20, atrPeriod: 14 }, ["high", "low", "close"], { upsideAtr: "decimal", downsideAtr: "decimal" }, 21, "wilder", "technical.breakout_atr_20_14"),
];

export type TechnicalIndicatorRegistryReason =
  | "DUPLICATE_INDICATOR_ID"
  | "MISSING_EXPLAIN_ID"
  | "MISSING_GOLDEN_FIXTURE"
  | "INVALID_WARMUP"
  | "MISSING_FAMILY";

export function validateTechnicalIndicatorRegistry(definitions: TechnicalIndicatorDefinition[]) {
  const reasons: Array<{ code: TechnicalIndicatorRegistryReason; subjectId: string }> = [];
  const seen = new Set<string>();
  for (const definition of definitions) {
    if (seen.has(definition.id)) reasons.push({ code: "DUPLICATE_INDICATOR_ID", subjectId: definition.id });
    seen.add(definition.id);
    if (!definition.explainId.trim()) reasons.push({ code: "MISSING_EXPLAIN_ID", subjectId: definition.id });
    if (definition.goldenFixtureIds.length === 0) reasons.push({ code: "MISSING_GOLDEN_FIXTURE", subjectId: definition.id });
    if (!Number.isInteger(definition.warmupBars) || definition.warmupBars < 1) reasons.push({ code: "INVALID_WARMUP", subjectId: definition.id });
  }
  const presentFamilies = new Set(definitions.map((definition) => definition.family));
  for (const family of ["trend", "momentum", "volatility", "participation", "structure"] as const) {
    if (!presentFamilies.has(family)) reasons.push({ code: "MISSING_FAMILY", subjectId: family });
  }
  return { state: reasons.length === 0 ? "ready" as const : "abstain" as const, reasons };
}

function stable(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stable).join(",")}]`;
  const object = value as Record<string, unknown>;
  return `{${Object.keys(object).sort().map((key) => `${JSON.stringify(key)}:${stable(object[key])}`).join(",")}}`;
}

export async function technicalIndicatorFingerprintSha256(definition: TechnicalIndicatorDefinition) {
  const digest = await globalThis.crypto.subtle.digest("SHA-256", new TextEncoder().encode(stable(definition)));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}
